/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import Colors from '@ziynx/theme/colors';
import BannerNotification from '@ziynx/components/notifications';
import { Helmet } from 'react-helmet-async';
import { ContentContainer } from '@ziynx/components/layout';
import ActionTypes from '@ziynx/action-types';
import Card from '@ziynx/components/cards';
import { withAppManager, LoadingSpinner } from '@ziynx/components/utilities';
import Headline from '@ziynx/components/headlines';
import { Heading } from '@ziynx/components/typography';
import type { MongoIdType } from '@ziynx/flow-types/globals';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { CompanyState } from '@ziynx/flow-types/companyProfile';
import RemoveModal from './RemoveModal';

type History = {
  push: (pathName: string, params?: { userStatus: string }) => void,
  replace: () => void,
};

type Location = {
  state: {
    from: string,
  }
};

type Props = $ReadOnly<{|
  /**
   * Navigation stack
   */
  history: History,
|}>;

type TeamMemberService = {
  removeInvite?: {
    fetching: boolean,
    success: boolean,
    error: any,
  },
  resendInvite?: {
    fetching: boolean,
    success: boolean,
    error: any,
  }
};

type TeamService = {
  fetching: boolean,
  success: boolean,
  error: any,
  isLastPage: boolean,
  page: number,
  pageSize: number,
  total: number,
  canInviteUsers: boolean,
  results: Array<{
    _id: MongoIdType,
    email: string,
    firstName: string,
    lastName: string,
    role: 'owners' | 'managers' | 'admins',
    userStatus: 'PENDING' | 'EXPIRED' | 'CONFIRMED',
    canEdit?: boolean,
    canRemove?: boolean,
  }>,
  removeUser?: {
    fetching: boolean,
    success: boolean,
    error: any,
  },
};

type DispatchType = {
  type: string,
  id?: string,
} => void;

const PageWrapper = styled.div`
  padding: ${({ isMobile }) => (isMobile ? '40px 16px' : '40px 40px 200px')};
  width: 100%;
  max-width: 688px;
  ${({ isMobile }) => isMobile && 'margin: auto;'}
  ::-webkit-scrollbar-track, ::-webkit-scrollbar-thumb, ::-webkit-scrollbar  {
    background-color: transparent;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const CardSection = styled.div`
  padding-top: 32px;
`;

const TableHeadSection = styled.div`
  padding: 0px 32px 24px;
  display: flex;
`;

const StyledHeading = styled(Heading)`
  padding-right: 171px;
`;

const CardWrapper = styled.div`
  ${({ lastChild }) => !lastChild && `
  padding-bottom: 24px;
  `};
`;

const StyledBannerWrapper = styled.div`
  position: fixed;
  z-index: 996;
  width: 100%;
  height: 0;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 32px;
`;

const _getUserStatus = (status: string): string => {
  switch (status) {
  case 'CONFIRMED':
    return '';
  case 'EXPIRED':
    return 'Invite expired';
  case 'PENDING':
  default:
    return 'Invite pending';
  }
};

const _transformRoles = (role: string) => {
  switch (role) {
  case 'owners':
    return 'Owner';
  case 'managers':
    return 'Manager';
  case 'admins':
  default:
    return 'Admin';
  }
};

const TeamScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;

  const location: Location = useLocation();

  const [isModalOpen, setModalOpen] = useState(false);

  const [removeMemberConfig, setRemoveMemberConfig] = useState({});

  const [globalNotification, setGlobalNotification] = useState({
    visible: false,
    text: undefined,
    variant: 'error',
  });

  const dispatch: DispatchType = useDispatch();

  const teamService: TeamService = useSelector(
    ({ team }) => ({
      ...team[ActionTypes.team.get.GROUP] || {},
      removeUser: team[ActionTypes.team.removeUser.GROUP] || {},
    }), shallowEqual,
  );

  const companyService: CompanyState = useSelector(({ company }) => {
    const myCompany = company?.me || {};

    return {
      ...myCompany,
    };
  }, shallowEqual);

  const teamMemberService: TeamMemberService = useSelector(
    ({ team }) => ({
      removeInvite: team[ActionTypes.teamMember.removeInvite.GROUP] || {},
      resendInvite: team[ActionTypes.teamMember.resendInvite.GROUP] || {},
    }), shallowEqual,
  );

  useEffect(() => {
    dispatch({
      type: ActionTypes.team.get.START,
      id: 'me',
      page: 0,
    });
  }, []);

  useEffect(() => {
    if (
      (teamService?.removeUser?.success || teamMemberService?.removeInvite?.success)
      && removeMemberConfig?.member) {
      setGlobalNotification({
        visible: true,
        variant: 'error',
        text: `${removeMemberConfig?.member} has been removed from your team.`,
      });
      setTimeout(() => {
        setGlobalNotification({
          visible: false,
        });
      }, 6000);
    }

    if (location?.state?.from === '/team/invite-member') {
      setGlobalNotification({
        visible: true,
        variant: 'success',
        // $FlowFixMe
        text: `${location?.state?.name} has been invited to ${companyService?.name}.`,
      });
      setTimeout(() => {
        setGlobalNotification({
          visible: false,
        });
      }, 6000);
      history.replace();
    }
  }, [teamService?.removeUser?.success,
    teamMemberService?.removeInvite?.success,
    location?.state?.from]);

  const onPaginate = (inView): void => {
    if (teamService?.isLastPage || teamService?.fetching || !inView) return;
    dispatch({
      type: ActionTypes.team.get.START,
      id: 'me',
      page: teamService?.page + 1,
    });
  };

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  const _renderCards = (): React$Node => !!teamService?.results?.length
  && teamService?.results?.map((member, index) => {
    const {
      firstName, lastName, email, userStatus, role, _id, canEdit = false, canRemove = false,
    } = member || {};

    const memberName = firstName?.concat(' ', lastName ?? '') ?? '';

    const editTeamPath = '/team/edit-member/';

    const teamMemberUrl = editTeamPath.concat(_id);
    const isInvite = userStatus === 'PENDING' || userStatus === 'EXPIRED';
    return (
      <CardWrapper lastChild={index === teamService?.results?.length - 1} key={_id}>
        <Card
          variant="table"
          title={{
            text: memberName,
          }}
          subtitle={{
            text: email,
          }}
          body={{
            text: _transformRoles(role),
          }}
          status={{
            pending: userStatus === 'PENDING' || userStatus === 'EXPIRED',
            text: _getUserStatus(userStatus),
            popoverPosition: 'topRight',
          }}
          popover={canEdit ? {
            ...canRemove ? {
              links: [{
                children: 'Edit information',
                onClick: () => history.push(teamMemberUrl, { userStatus }),
              },
              isInvite ? {
                children: 'Resend invite',
                onClick: () => {
                  dispatch({
                    type: ActionTypes.teamMember.resendInvite.START,
                    token: _id,
                  });
                  setGlobalNotification({
                    visible: true,
                    variant: 'success',
                    // eslint-disable-next-line prefer-template
                    text: 'The invite has been resent to ' + memberName + ' (' + email + ').',
                  });
                  setTimeout(() => {
                    setGlobalNotification({
                      visible: false,
                    });
                  }, 6000);
                  dispatch({ type: ActionTypes.teamMember.clear });
                },
              } : { children: null },
              {
                children: isInvite ? 'Remove invite' : 'Remove member',
                onClick: () => {
                  setRemoveMemberConfig({ member: memberName, memberId: _id, userStatus });
                  setModalOpen(true);
                },
                color: Colors.SecondaryRed,
              }],
              position: 'bottomLeft',
            } : {
              links: [{
                children: 'Edit information',
                onClick: () => history.push(teamMemberUrl),
              }],
              position: 'bottomLeft',
            },
          } : undefined}
        />
      </CardWrapper>
    );
  });

  return (
    <>
      {globalNotification?.visible && (
        <StyledBannerWrapper>
          <BannerNotification
            animated
            showCloseIcon
            variant={globalNotification.variant}
            timeout={5000}
          >
            {globalNotification?.text}
          </BannerNotification>
        </StyledBannerWrapper>
      )}
      <Helmet>
        <title>Team | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={isMobile ? 84 : undefined}>
        <RemoveModal
          open={isModalOpen}
          removeMemberConfig={removeMemberConfig}
          onClose={() => setModalOpen(false)}
          buttonGroup={{
            destructive: {
              children: 'Remove member',
            },
            primary: {
              children: 'Cancel',
              onClick: () => setModalOpen(false),
            },
          }}

        />
        <PageWrapper isMobile={isMobile}>
          {viewport === 'mobileSmall'
            ? (
              <Headline
                variant="page"
                title={{
                  text: 'Team',
                  color: Colors.GrayVeryDark,
                }}
                squircleButton={teamService?.canInviteUsers ? {
                  onClick: () => history.push('team/invite-member'),
                } : undefined}
              />
            )
            : (
              <Headline
                variant="page"
                title={{
                  text: 'Team',
                  color: Colors.GrayVeryDark,
                }}
                textLink={teamService?.canInviteUsers ? {
                  children: 'Invite team member',
                  large: true,
                  color: Colors.PrimaryBlue,
                  onClick: () => history.push('team/invite-member'),
                } : undefined}
              />
            )}
          <CardSection>
            {!isMobile
            && (
              <TableHeadSection>
                <StyledHeading color={Colors.GrayDark} small>Name</StyledHeading>
                <Heading color={Colors.GrayDark} small>Email</Heading>
              </TableHeadSection>
            )}
            {_renderCards()}
          </CardSection>
          <InView as="div" onChange={onPaginate}>
            {!teamService?.isLastPage && (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            )}
          </InView>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(TeamScreen): React$ComponentType<ManagedProps<Props>>);
